import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { LEAD_SOURCES, STAGES } from "../lib/constants";
import { Field, Options, useBusy } from "./common";
import { AutofillButton } from "./ai/AutofillDialog";

const EMPTY = {
  first_name: "", last_name: "", phone: "", email: "", property_address: "", billing_address: "",
  preferred_language: "en", service_requested: "", lead_source: "Google", notes: "", stage: "new_lead",
};

export default function CustomerForm({ initial, onClose, onSaved }) {
  const { t, lang } = useLang();
  const [f, setF] = useState({ ...EMPTY, ...(initial || {}) });
  const [busy, run] = useBusy();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = (e) =>
    run(async () => {
      e.preventDefault();
      try {
        const { data } = initial?.id ? await api.put(`/customers/${initial.id}`, f) : await api.post("/customers", f);
        toast.success(t("Customer saved", "Cliente guardado"));
        onSaved?.(data);
        onClose();
      } catch (err) {
        toast.error(errMsg(err));
      }
    });
  const inp = (k, l, type = "text") => (
    <Field label={l}>
      <input className="inp" type={type} value={f[k]} onChange={set(k)} data-testid={`customer-form-${k}`} />
    </Field>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="customer-form-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? t("Edit Customer", "Editar cliente") : t("New Customer", "Nuevo cliente")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="grid gap-3">
          <AutofillButton form="customer" current={f} ctx={{ customer_id: initial?.id }} onApply={(v) => setF((x) => ({ ...x, ...v }))} className="w-full h-11" />
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("First name *", "Nombre *")}>
              <input className="inp" required value={f.first_name} onChange={set("first_name")} data-testid="customer-form-first_name" />
            </Field>
            {inp("last_name", t("Last name", "Apellido"))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {inp("phone", t("Phone", "Teléfono"), "tel")}
            {inp("email", t("Email", "Correo"), "email")}
          </div>
          {inp("property_address", t("Property address", "Dirección de la propiedad"))}
          {inp("billing_address", t("Billing address (if different)", "Dirección de facturación (si es diferente)"))}
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("Preferred language", "Idioma preferido")}>
              <select className="inp" value={f.preferred_language} onChange={set("preferred_language")} data-testid="customer-form-language">
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </Field>
            <Field label={t("Lead source", "Fuente")}>
              <select className="inp" value={f.lead_source} onChange={set("lead_source")} data-testid="customer-form-lead-source">
                {LEAD_SOURCES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          {inp("service_requested", t("Service requested", "Servicio solicitado"))}
          <Field label={t("Pipeline stage", "Etapa")}>
            <select className="inp" value={f.stage} onChange={set("stage")} data-testid="customer-form-stage">
              <Options items={STAGES} lang={lang} />
            </select>
          </Field>
          <Field label={t("Notes", "Notas")} ai={{ form: "customer", field: "notes", value: f.notes, onInsert: (v) => setF((x) => ({ ...x, notes: v })), ctx: { customer_id: initial?.id } }}>
            <textarea className="inp" value={f.notes} onChange={set("notes")} data-testid="customer-form-notes" />
          </Field>
          <button className="btn-primary w-full" disabled={busy} data-testid="customer-form-save-btn">
            {t("Save Customer", "Guardar cliente")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
